import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

/**
 * Dropdown aksi per-baris yang dirender lewat portal (document.body)
 * dengan position: fixed, sehingga tidak ter-clip oleh container
 * overflow milik CSmartTable (responsive).
 *
 * Pemakaian:
 * <RowActionMenu toggle={<CImage fluid src={ThreeDotsIconMenu} />}>
 *   <RowActionItem onClick={...}>Edit Asset</RowActionItem>
 * </RowActionMenu>
 */
export const RowActionItem = ({ onClick, children }) => (
  <button
    type="button"
    className="dropdown-item"
    style={{ cursor: 'pointer' }}
    onClick={onClick}
  >
    {children}
  </button>
)

const GAP = 4
const EDGE = 8

const RowActionMenu = ({ toggle, children }) => {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const buttonRef = useRef(null)
  const menuRef = useRef(null)

  const updatePosition = useCallback(() => {
    const button = buttonRef.current
    const menu = menuRef.current
    if (!button || !menu) return

    const b = button.getBoundingClientRect()
    const m = menu.getBoundingClientRect()

    // default: di bawah tombol; kalau tidak muat, pindah ke atas
    let top = b.bottom + GAP
    const overflowBottom = top + m.height > window.innerHeight - EDGE
    const spaceAbove = b.top - GAP - m.height >= EDGE
    if (overflowBottom && spaceAbove) {
      top = b.top - GAP - m.height
    }

    // rata kanan dengan tombol, jangan keluar layar
    let left = b.right - m.width
    if (left < EDGE) left = EDGE
    if (left + m.width > window.innerWidth - EDGE) {
      left = window.innerWidth - EDGE - m.width
    }

    setPos({ top, left })
  }, [])

  // ukur & posisikan sebelum paint supaya tidak berkedip
  useLayoutEffect(() => {
    if (open) updatePosition()
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return undefined

    const handleOutside = (e) => {
      if (menuRef.current?.contains(e.target)) return
      if (buttonRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const close = () => setOpen(false)
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('keydown', handleKey)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close, true) // capture: termasuk scroll di dalam tabel

    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('keydown', handleKey)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [open])

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="btn"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation() // jangan memicu onRowClick
          setOpen((prev) => !prev)
        }}
        style={{
          border: 'none',
          boxShadow: 'none',
          background: '#FFF',
          borderRadius: '.3rem',
        }}
      >
        {toggle}
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="dropdown-menu show"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              right: 'auto',
              bottom: 'auto',
              margin: 0,
              transform: 'none',
              zIndex: 2000,
            }}
            onClick={(e) => {
              e.stopPropagation() // event portal tetap bubble ke <tr>, hentikan di sini
              setOpen(false) // tutup menu setelah item diklik
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}

export default RowActionMenu